import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AdminLayout } from '../components/AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCOPES = [
  { key: 'newsroom',          label: 'Newsroom' },
  { key: 'education',         label: 'Education' },
  { key: 'healthcare',        label: 'Healthcare' },
  { key: 'business-finance',  label: 'Business & Finance' },
  { key: 'government',        label: 'Government' },
  { key: 'technology-ai',     label: 'Technology & AI' },
  { key: 'workforce-labor',   label: 'Workforce & Labor' },
];

const PROVIDERS = [
  { value: 'alugha',       label: 'Alugha',                  placeholder: 'https://alugha.com/videos/{id}  or  bare video ID', help: 'Paste the Alugha watch-page URL, embed URL, or a bare video ID.' },
  { value: 'youtube',      label: 'YouTube',                 placeholder: 'https://www.youtube.com/watch?v=...  or  bare 11-char ID', help: 'Any YouTube URL form (watch, youtu.be, embed, shorts) or a bare video ID. Renders privacy-enhanced via youtube-nocookie.com.' },
  { value: 'vimeo',        label: 'Vimeo',                   placeholder: 'https://vimeo.com/123456789  or  bare numeric ID', help: 'A Vimeo URL or a bare numeric video ID.' },
  { value: 'direct',       label: 'Direct File (MP4 / HLS)', placeholder: 'https://.../video.mp4  or  .m3u8', help: 'Must be an https URL ending in .mp4 or .m3u8.' },
  { value: 'embed_script', label: 'Custom Embed Markup',     placeholder: '<iframe src="https://..."></iframe>', help: 'Paste a single <iframe> tag with an https src from an allowlisted domain (alugha.com, youtube.com / youtube-nocookie.com, vimeo.com). Script tags and event-handler attributes are rejected at save.' },
];

const NAVY  = '#0D2137';
const TEAL  = '#1B6B72';
const CARD  = '#ffffff';
const MUTED = '#5b6066';
const BORDER = '#e8e4db';

function authHeader() {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const EMPTY_VIDEO = {
  title: '', description: '', provider: 'youtube', video_ref: '',
  poster_image_url: '', duration_label: '', cta_text: 'Watch Now',
  is_active: true, starts_at: '', ends_at: '',
};

function statusBadge(video, liveId) {
  const now = new Date();
  if (video.id === liveId) return { label: 'LIVE NOW', bg: '#e2f4ea', color: '#1d7a50' };
  if (!video.is_active) return { label: 'Inactive', bg: '#f1f0ee', color: MUTED };
  if (video.starts_at && new Date(video.starts_at) > now) return { label: 'Scheduled', bg: '#e8f0fb', color: '#2563a8' };
  if (video.ends_at && new Date(video.ends_at) < now) return { label: 'Expired', bg: '#faeaea', color: '#a83232' };
  return { label: 'Active (queued)', bg: '#fbf4e2', color: '#8a6a1a' };
}

// ─── Live Preview Pane ───────────────────────────────────────────────────────
function PreviewPane({ provider, videoRef, posterUrl }) {
  const [preview, setPreview] = useState(null); // { embed } | { error }
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!videoRef || !videoRef.trim()) { setPreview(null); return; }
    setChecking(true);
    const t = setTimeout(() => {
      axios.post(`${API}/admin/videos/resolve`, { provider, video_ref: videoRef, poster_image_url: posterUrl || null }, { headers: authHeader() })
        .then(r => setPreview({ embed: r.data.embed }))
        .catch(err => setPreview({ error: (err.response && err.response.data && err.response.data.detail) || 'Could not resolve embed' }))
        .finally(() => setChecking(false));
    }, 450);
    return () => clearTimeout(t);
  }, [provider, videoRef, posterUrl]);

  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, padding: 14, background: '#fafaf8' }}>
      <div style={{ fontWeight: 700, fontSize: 11, letterSpacing: '0.6px', textTransform: 'uppercase', color: TEAL, marginBottom: 10 }}>Live Preview</div>
      {!videoRef ? (
        <div style={{ fontSize: 12.5, color: MUTED }}>Enter a video reference to preview the resolved embed.</div>
      ) : checking && !preview ? (
        <div style={{ fontSize: 12.5, color: MUTED }}>Resolving…</div>
      ) : preview && preview.error ? (
        <div style={{ fontSize: 12.5, color: '#a83232', fontWeight: 600 }} data-testid="video-preview-error">{preview.error}</div>
      ) : preview && preview.embed ? (
        <div>
          <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: NAVY, borderRadius: 6, overflow: 'hidden' }} data-testid="video-preview-ok">
            {preview.embed.type === 'iframe' ? (
              <iframe src={preview.embed.src} title="preview" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} allow={preview.embed.allow} />
            ) : (
              <video src={preview.embed.src} poster={preview.embed.poster || undefined} controls style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
          </div>
          <div style={{ fontSize: 11, color: MUTED, marginTop: 6, wordBreak: 'break-all' }}>Resolved src: {preview.embed.src}</div>
        </div>
      ) : null}
    </div>
  );
}

// ─── Add / Edit Form ─────────────────────────────────────────────────────────
function VideoForm({ scopeKey, initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY_VIDEO);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isEdit = !!(initial && initial.id);

  const set = (field, val) => setForm(prev => ({ ...prev, [field]: val }));
  const providerMeta = PROVIDERS.find(p => p.value === form.provider) || PROVIDERS[0];

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        scope: scopeKey,
        title: form.title,
        description: form.description || null,
        provider: form.provider,
        video_ref: form.video_ref,
        poster_image_url: form.poster_image_url || null,
        duration_label: form.duration_label || null,
        cta_text: form.cta_text || 'Watch Now',
        is_active: form.is_active,
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
      };
      if (isEdit) {
        await axios.put(`${API}/admin/videos/${initial.id}`, payload, { headers: authHeader() });
      } else {
        await axios.post(`${API}/admin/videos`, payload, { headers: authHeader() });
      }
      onSaved();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.detail) || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { width: '100%', padding: '8px 11px', border: '1px solid #d8d3c8', borderRadius: 4, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 5 };

  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, padding: 18, background: '#fff', marginBottom: 16 }} data-testid={`video-form-${scopeKey}`}>
      <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 20 }}>
        <div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Title *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} style={inputStyle} data-testid="video-title-input" />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Description</label>
            <textarea value={form.description || ''} onChange={e => set('description', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} data-testid="video-description-input" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Provider *</label>
              <select value={form.provider} onChange={e => { set('provider', e.target.value); }} style={inputStyle} data-testid="video-provider-select">
                {PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Duration Label</label>
              <input value={form.duration_label || ''} onChange={e => set('duration_label', e.target.value)} placeholder="e.g. 6:24" style={inputStyle} data-testid="video-duration-input" />
            </div>
          </div>
          <div style={{ marginBottom: 6 }}>
            <label style={labelStyle}>Video Reference *</label>
            <input value={form.video_ref} onChange={e => set('video_ref', e.target.value)} placeholder={providerMeta.placeholder} style={inputStyle} data-testid="video-ref-input" />
          </div>
          <div style={{ fontSize: 11, color: MUTED, marginBottom: 14, lineHeight: 1.4 }}>{providerMeta.help}</div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Poster Image URL</label>
            <input value={form.poster_image_url || ''} onChange={e => set('poster_image_url', e.target.value)} placeholder="https://ik.imagekit.io/…" style={inputStyle} data-testid="video-poster-input" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>CTA Text</label>
              <input value={form.cta_text || ''} onChange={e => set('cta_text', e.target.value)} placeholder="Watch Now" style={inputStyle} data-testid="video-cta-input" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', paddingTop: 18 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 600, color: MUTED, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} style={{ width: 14, height: 14 }} data-testid="video-active-toggle" />
                Active
              </label>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 }}>
            <div>
              <label style={labelStyle}>Starts At (optional)</label>
              <input type="datetime-local" value={form.starts_at ? form.starts_at.slice(0, 16) : ''} onChange={e => set('starts_at', e.target.value)} style={inputStyle} data-testid="video-starts-input" />
            </div>
            <div>
              <label style={labelStyle}>Ends At (optional)</label>
              <input type="datetime-local" value={form.ends_at ? form.ends_at.slice(0, 16) : ''} onChange={e => set('ends_at', e.target.value)} style={inputStyle} data-testid="video-ends-input" />
            </div>
          </div>
        </div>
        <div>
          <PreviewPane provider={form.provider} videoRef={form.video_ref} posterUrl={form.poster_image_url} />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 16, marginTop: 12, borderTop: '1px solid #ede9df' }}>
        <button
          onClick={handleSave}
          disabled={saving || !form.title || !form.video_ref}
          style={{ background: (saving || !form.title || !form.video_ref) ? '#c9c4b8' : NAVY, color: '#fff', border: 'none', borderRadius: 4, padding: '9px 20px', fontWeight: 700, fontSize: 13.5, cursor: (saving || !form.title || !form.video_ref) ? 'default' : 'pointer' }}
          data-testid="video-save-btn"
        >{saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Video'}</button>
        <button onClick={onCancel} style={{ background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 4, padding: '9px 16px', fontWeight: 600, fontSize: 13, color: MUTED, cursor: 'pointer' }} data-testid="video-cancel-btn">Cancel</button>
        {error && <span style={{ color: '#a83232', fontSize: 12.5, fontWeight: 600 }} data-testid="video-save-error">{error}</span>}
      </div>
    </div>
  );
}

// ─── Scope Panel ─────────────────────────────────────────────────────────────
function ScopePanel({ scopeKey }) {
  const [videos, setVideos] = useState([]);
  const [liveId, setLiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | video object

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/videos`, { params: { scope: scopeKey }, headers: authHeader() }),
      axios.get(`${API}/videos/featured`, { params: { scope: scopeKey } }),
    ]).then(([listRes, liveRes]) => {
      setVideos(listRes.data || []);
      setLiveId(liveRes.data && liveRes.data.active ? liveRes.data.id : null);
    }).finally(() => setLoading(false));
  }, [scopeKey]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this video permanently?')) return;
    await axios.delete(`${API}/admin/videos/${id}`, { headers: authHeader() });
    load();
  };

  if (loading) return <div style={{ padding: 40, color: MUTED, textAlign: 'center' }}>Loading…</div>;

  return (
    <div>
      {editing && (
        <VideoForm
          scopeKey={scopeKey}
          initial={editing === 'new' ? null : editing}
          onSaved={() => { setEditing(null); load(); }}
          onCancel={() => setEditing(null)}
        />
      )}

      {!editing && (
        <button
          onClick={() => setEditing('new')}
          style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: 4, padding: '9px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 18 }}
          data-testid={`add-video-${scopeKey}`}
        >+ Add Video</button>
      )}

      {videos.length === 0 ? (
        <div style={{ padding: '22px 16px', border: '1px dashed #d8d3c8', borderRadius: 6, color: MUTED, fontSize: 13, textAlign: 'center' }}>
          No videos configured for this scope. The "Video of the Week" widget renders nothing until one is added.
        </div>
      ) : (
        videos.map(v => {
          const badge = statusBadge(v, liveId);
          return (
            <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 14, border: `1px solid ${BORDER}`, borderRadius: 6, padding: '12px 16px', marginBottom: 10, background: CARD }} data-testid={`video-row-${v.id}`}>
              <span style={{ background: badge.bg, color: badge.color, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.5px', padding: '4px 9px', borderRadius: 6, flexShrink: 0 }}>{badge.label}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: NAVY }}>{v.title}</div>
                <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>{v.provider} · updated {new Date(v.updated_at).toLocaleString()}</div>
              </div>
              <button onClick={() => setEditing(v)} style={{ background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 4, padding: '6px 14px', fontWeight: 600, fontSize: 12, color: TEAL, cursor: 'pointer' }} data-testid={`edit-video-${v.id}`}>Edit</button>
              <button onClick={() => handleDelete(v.id)} style={{ background: '#fee2e2', border: 'none', borderRadius: 4, padding: '6px 14px', fontWeight: 600, fontSize: 12, color: '#991b1b', cursor: 'pointer' }} data-testid={`delete-video-${v.id}`}>Delete</button>
            </div>
          );
        })
      )}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function AdminVideoManagerPage() {
  const [activeScope, setActiveScope] = useState('newsroom');

  return (
    <AdminLayout>
      <div style={{ maxWidth: 980, margin: '0 auto', padding: '28px 0 48px', fontFamily: "'Public Sans', sans-serif" }} data-testid="admin-video-manager-page">
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontFamily: "'Newsreader', Georgia, serif", fontWeight: 500, fontSize: 32, color: NAVY, margin: '0 0 6px' }}>Video Manager</h1>
          <p style={{ fontSize: 13, color: MUTED, margin: 0 }}>Control the "Video of the Week" widget per scope. One active, in-window video renders per scope (most recently updated wins).</p>
        </div>

        <div style={{ display: 'flex', borderBottom: '2px solid #e8e4db', marginBottom: 28, gap: 2, flexWrap: 'wrap' }}>
          {SCOPES.map(s => (
            <button
              key={s.key}
              onClick={() => setActiveScope(s.key)}
              style={{
                background: activeScope === s.key ? NAVY : 'transparent',
                color: activeScope === s.key ? '#fff' : MUTED,
                border: 'none',
                borderRadius: '4px 4px 0 0',
                padding: '9px 16px',
                fontWeight: 600,
                fontSize: 12.5,
                cursor: 'pointer',
                fontFamily: 'inherit',
                marginBottom: -2,
                borderBottom: activeScope === s.key ? `2px solid ${NAVY}` : '2px solid transparent',
              }}
              data-testid={`video-tab-${s.key}`}
            >{s.label}</button>
          ))}
        </div>

        <div style={{ background: CARD, border: '1px solid #e8e4db', borderRadius: 8, padding: '24px 28px' }}>
          <ScopePanel key={activeScope} scopeKey={activeScope} />
        </div>
      </div>
    </AdminLayout>
  );
}
